import { useEffect, useRef, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { api } from "@/lib/api";
import ProfileRadar from "@/components/ProfileRadar";
import DiagnosticoPanel from "@/components/DiagnosticoPanel";
import StudentExtras from "@/components/StudentExtras";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { ArrowLeft, BookOpen, Brain, HeartPulse, Sparkles, ClipboardList, PenSquare, Lightbulb, FileText, Loader2, FolderOpen, ChevronDown, ChevronRight, RefreshCw } from "lucide-react";
import { toast } from "sonner";

// Solo se muestran mensajes ACCIONABLES del servidor (faltan CEP/CAD, permisos); cualquier fallo técnico → mensaje amable.
const friendly = (e, fallback) => {
  const st = e?.response?.status;
  const d = e?.response?.data?.detail;
  return (st === 422 || st === 400 || st === 403) && typeof d === "string" ? d : fallback;
};

const TREE_ORDER = ["ruta_ensenanza", "cep", "cad", "integral", "ice", "andamiaje", "plan_individual"];

function ReportTree({ items, labels, baseRoute, fmtEta, onRetry }) {
  const [open, setOpen] = useState({});
  const [rootOpen, setRootOpen] = useState(true);
  const groups = TREE_ORDER.map((type) => {
    const all = items.filter((r) => r.type === type).sort((a, b) => String(b.generated_at || "").localeCompare(String(a.generated_at || "")));
    const ready = all.filter((r) => r.status === "generated");
    const pending = all.filter((r) => r.status !== "generated");
    // Con un informe listo, los intentos fallidos o repetidos no se muestran (nunca se enseñan errores).
    const children = ready.length ? [...pending.filter((r) => r.status === "queued" || r.status === "generating"), ...ready] : pending.slice(0, 1);
    return { type, ready, children, inProgress: !ready.length && all.length > 0, working: pending.some((r) => r.status === "queued" || r.status === "generating") };
  }).filter((g) => g.children.length);
  const readyTotal = groups.reduce((n, g) => n + g.ready.length, 0);
  const allOpen = groups.length > 0 && groups.every((g) => open[g.type]);

  return (
    <div className="neo-card p-5" data-testid="expediente-informes">
      <button type="button" onClick={() => setRootOpen((o) => !o)} data-testid="expediente-root-toggle"
        className="w-full flex items-center gap-3 text-left">
        <span className="w-10 h-10 rounded-2xl grid place-items-center text-white shrink-0" style={{ background: "linear-gradient(145deg,#C257AE,#A43790)" }}><FolderOpen size={18} /></span>
        <span className="flex-1 min-w-0">
          <span className="block text-lg font-brand text-[#1F2547]">Expediente de Informes</span>
          <span className="block text-xs text-[#6E7591]">{readyTotal} informe(s) listo(s) · formato oficial JASQ®</span>
        </span>
        {rootOpen ? <ChevronDown size={18} className="text-[#6E7591]" /> : <ChevronRight size={18} className="text-[#6E7591]" />}
      </button>

      {rootOpen && (
        groups.length === 0 ? (
          <p className="text-sm text-[#6E7591] py-6 text-center">Aún no hay informes. Usa «Generar Todos los Informes» o «Generar Ruta de Enseñanza».</p>
        ) : (
          <div className="mt-4">
            <div className="flex justify-end mb-2">
              <button type="button" data-testid="expediente-toggle-all"
                onClick={() => setOpen(allOpen ? {} : Object.fromEntries(groups.map((g) => [g.type, true])))}
                className="text-xs font-extrabold text-[#A43790] hover:underline">{allOpen ? "Contraer todo" : "Expandir todo"}</button>
            </div>
            <ul className="space-y-2 border-l-2 border-[#D3D9E6] ml-5 pl-4">
              {groups.map((g) => {
                const isOpen = !!open[g.type];
                return (
                  <li key={g.type} data-testid={`expediente-${g.type}`}>
                    <button type="button" onClick={() => setOpen((o) => ({ ...o, [g.type]: !o[g.type] }))}
                      data-testid={`expediente-node-${g.type}`}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all ${isOpen ? "neo-pressed" : "neo-flat"}`}>
                      {isOpen ? <ChevronDown size={15} className="text-[#6E7591]" /> : <ChevronRight size={15} className="text-[#6E7591]" />}
                      <FileText size={16} className="text-[#A43790] shrink-0" />
                      <span className="flex-1 min-w-0 text-sm font-bold text-[#1F2547] truncate">{labels[g.type] || g.type}</span>
                      {g.ready.length > 0 && <span className="text-[11px] font-extrabold text-[#05A859] bg-[#D8F1E4] rounded-full px-2 py-0.5">{g.ready.length > 1 ? `${g.ready.length} versiones` : "Listo"}</span>}
                      {g.inProgress && <span className="text-[11px] font-extrabold text-[#C98A00] bg-[#FFF1CC] rounded-full px-2 py-0.5 inline-flex items-center gap-1"><Loader2 size={11} className="animate-spin" /> En progreso</span>}
                    </button>
                    {isOpen && (
                      <ul className="mt-2 ml-4 pl-4 border-l-2 border-[#E1E5F0] space-y-2">
                        {g.children.map((r) => {
                          const done = r.status === "generated";
                          const stuck = !done && r.status === "failed";
                          return (
                            <li key={r.id} className="flex items-center justify-between gap-3 py-1.5" data-testid={`expediente-item-${r.id}`}>
                              <div className="min-w-0">
                                <p className="text-sm font-bold text-[#1F2547] truncate">{labels[r.type] || r.type} · v{r.version}</p>
                                <p className="text-[11px] text-[#6E7591]">
                                  {done ? (r.generated_at ? new Date(r.generated_at).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" }) : "Listo")
                                    : `En progreso${r.eta_seconds ? ` · ${fmtEta(r.eta_seconds)}` : ""}`}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                {done && (
                                  <>
                                    <Link to={`${baseRoute}/ruta-ensenanza/${r.id}`} data-testid={`expediente-ver-${r.type}`}
                                      className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-lg bg-[#A43790] text-white hover:bg-[#8A2E79]">👁 Ver</Link>
                                    {r.pdf_url && <a href={r.pdf_url} target="_blank" rel="noreferrer" data-testid={`expediente-pdf-${r.type}`}
                                      className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-lg neo-flat text-[#A43790]">📥 PDF</a>}
                                  </>
                                )}
                                {!done && !stuck && <Loader2 size={16} className={`animate-spin ${r.status === "queued" ? "text-[#B7BECD]" : "text-[#A43790]"}`} />}
                                {stuck && (
                                  <button type="button" onClick={() => onRetry(r.type)} title="Reintentar" aria-label="Reintentar"
                                    data-testid={`expediente-retry-${r.type}`} className="w-8 h-8 rounded-full neo-flat grid place-items-center text-[#A43790]"><RefreshCw size={14} /></button>
                                )}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )
      )}
    </div>
  );
}

export default function EstudianteDetalle() {
  const { id } = useParams();
  const { pathname: _path } = useLocation();
  const _seg = _path.split("/")[1];
  const detailBase = `/${["admin", "docente", "franquiciado"].includes(_seg) ? _seg : "docente"}/estudiante/${id}`; // v5.6.1 · botones Evaluar 1:1 / Armar ruta según el rol
  const [profile, setProfile] = useState(null);
  const [plan, setPlan] = useState(null);
  const [progress, setProgress] = useState(null);
  const [recs, setRecs] = useState([]);
  const [rutaBusy, setRutaBusy] = useState(false);
  const [rutaReport, setRutaReport] = useState(null);
  const [expediente, setExpediente] = useState([]);
  const baseRoute = typeof window !== "undefined" && window.location.pathname.includes("/admin/") ? "/admin" : typeof window !== "undefined" && window.location.pathname.includes("/franquiciado/") ? "/franquiciado" : "/docente";
  const backRoute = baseRoute === "/admin" ? "/admin/perfiles-estudiantes" : baseRoute === "/franquiciado" ? "/franquiciado/perfiles-estudiantes" : "/docente/perfiles-estudiantes";
  const REPORT_LABELS = {
    ruta_ensenanza: "Ruta de Enseñanza", cep: "Análisis CEP", cad: "Análisis CAD",
    integral: "Análisis Integral", ice: "Índice de Cambio Emocional (ICE)",
    andamiaje: "Mapa de Andamiaje", plan_individual: "Plan Individual de Intervención",
  };

  const [allBusy, setAllBusy] = useState(false);

  const loadExpediente = () => api.get(`/diagnostics/students/${id}/reports`).then(({ data }) => setExpediente(data.items || [])).catch(() => setExpediente([]));
  useEffect(() => { loadExpediente(); }, [id]);

  // v5.1.0 · Seguimiento LIVIANO de la cola: consulta /reports/status (sin firmar URLs) con intervalo adaptable,
  // retroceso exponencial si el servidor está ocupado (520/5xx) y SIN abandonar el seguimiento por un fallo puntual.
  const [queueInfo, setQueueInfo] = useState(null);   // {active,total,eta_seconds,queue_total,concurrency,...}
  const [serverBusy, setServerBusy] = useState(false);
  const [queuePaused, setQueuePaused] = useState(false);
  const aliveRef = useRef(true);
  const trackingRef = useRef(false);
  const doneRef = useRef(null);
  useEffect(() => { aliveRef.current = true; return () => { aliveRef.current = false; }; }, []);

  const fmtEta = (sec) => {
    if (!sec || sec <= 0) return "";
    if (sec < 60) return "≈ menos de 1 min";
    return `≈ ${Math.round(sec / 60)} min`;
  };

  const trackReports = (onDone) => {
    doneRef.current = onDone;
    if (trackingRef.current) return;
    trackingRef.current = true;
    const started = Date.now();
    let fails = 0;
    const finish = async (timedOut) => {
      trackingRef.current = false;
      setQueueInfo(null); setServerBusy(false);
      let items = [];
      try { const { data } = await api.get(`/diagnostics/students/${id}/reports`); items = data.items || []; setExpediente(items); } catch { /* se mostrará lo último conocido */ }
      if (aliveRef.current) doneRef.current?.(items, timedOut);
    };
    const poll = async () => {
      if (!aliveRef.current) { trackingRef.current = false; return; }
      let delay = 5000;
      try {
        const { data } = await api.get(`/diagnostics/students/${id}/reports/status`);
        fails = 0; setServerBusy(false); setQueueInfo(data);
        const byId = Object.fromEntries((data.items || []).map((i) => [i.id, i]));
        setExpediente((prev) => {
          const known = new Set(prev.map((p) => p.id));
          return [...prev.map((p) => (byId[p.id] ? { ...p, ...byId[p.id] } : p)), ...(data.items || []).filter((i) => !known.has(i.id))];
        });
        if (data.active === 0) { await finish(false); return; }
        // v5.6.5 · cola en PAUSA (la reanuda el administrador): no dejar el botón «Generando…» para siempre
        if (data.paused) { setQueuePaused(true); await finish(false); setQueueInfo(data); return; }
        if (Date.now() - started > 40 * 60 * 1000) { await finish(true); return; }
        delay = document.hidden ? 15000 : 5000;
      } catch {
        fails += 1; setServerBusy(true);
        delay = Math.min(60000, 5000 * 2 ** fails);
        if (fails >= 12) { await finish(true); return; }
      }
      setTimeout(poll, delay);
    };
    setTimeout(poll, 2500);
  };

  // Si ya hay informes en cola/generándose (p. ej. recargaste la página), se retoma el seguimiento.
  useEffect(() => {
    api.get(`/diagnostics/students/${id}/reports/status`).then(({ data }) => {
      if (data.active > 0 && data.paused) { setQueuePaused(true); setQueueInfo(data); }
      else if (data.active > 0) {
        setAllBusy(true);
        trackReports((items) => {
          setAllBusy(false);
          const failed = items.filter((i) => i.status === "failed");
          if (failed.length) toast.info(`${failed.length} informe(s) siguen en progreso.`, { duration: 8000 });
          else toast.success("Informes listos en el expediente.");
        });
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const [tab, setTab] = useState("resumen");
  const TABS = [
    { id: "resumen", label: "Resumen" },
    { id: "perfiles", label: "Perfiles" },
    { id: "evolucion", label: "Evolución y plan" },
    { id: "informes", label: "Informes" },
  ];

  // Reintento manual (solo icono, sin mostrar errores): vuelve a encolar ese informe.
  const reintentarInforme = async (type) => {
    try {
      await api.post(`/diagnostics/students/${id}/reports/generate`, { types: [type], force: true });
      toast.info("Informe en progreso.");
      setAllBusy(true);
      trackReports(() => { setAllBusy(false); });
    } catch { toast.info("Informe en progreso."); }
  };

  const generarTodosInformes = async () => {
    setQueuePaused(false);
    setAllBusy(true);
    try {
      const { data: result } = await api.post(`/diagnostics/students/${id}/reports/generate`, { force: false });
      if ((result.total_queued || 0) === 0) {
        toast.info("Todos los informes ya están generados o en proceso.");
        setAllBusy(false);
        loadExpediente();
        return;
      }
      toast.success(`${result.total_queued} informe(s) en cola. Puedes seguir trabajando: se generan en segundo plano y verás el tiempo estimado abajo.`, { duration: 8000 });
      loadExpediente();
      trackReports((items, timedOut) => {
        setAllBusy(false);
        if (timedOut) { toast.info("Algunos informes siguen en proceso. Recarga en unos minutos."); return; }
        const generated = items.filter((i) => i.status === "generated").length;
        const failed = items.filter((i) => i.status === "failed");
        if (failed.length) toast.info(`${failed.length} informe(s) siguen en progreso.`, { duration: 8000 });
        toast.success(`${generated} informe(s) listos en el expediente.`);
      });
    } catch (e) {
      setAllBusy(false);
      toast.info(friendly(e, "Los informes siguen en cola. Intenta de nuevo en unos minutos."));
    }
  };

  const generarRutaEnsenanza = async () => {
    setQueuePaused(false);
    setRutaBusy(true);
    setRutaReport(null);
    try {
      await api.post(`/diagnostics/students/${id}/reports/generate`, { types: ["ruta_ensenanza"], force: true });
      toast.success("Ruta de Enseñanza en cola… la IA la redacta en cuanto le toca (≈1–2 min).", { duration: 6000 });
      loadExpediente();
      trackReports((items, timedOut) => {
        setRutaBusy(false);
        const r = items.find((i) => i.type === "ruta_ensenanza");
        if (r?.status === "generated") {
          setRutaReport(r);
          toast.success("¡Ruta de Enseñanza lista!");
          if (r.pdf_url) window.open(r.pdf_url, "_blank");
        } else if (timedOut) {
          toast.info("La Ruta de Enseñanza sigue en progreso. Revisa en unos minutos.");
        } else {
          toast.info("La Ruta de Enseñanza sigue en progreso.");
        }
      });
    } catch (e) {
      setRutaBusy(false);
      toast.info(friendly(e, "La Ruta de Enseñanza sigue en cola. Intenta de nuevo en unos minutos."));
    }
  };

  // v5.7.1 · carga con reintentos: antes, si UNA de las 4 consultas fallaba (p. ej. al volver atrás desde un informe
  // con el servidor ocupado) la pantalla se quedaba en «Cargando…» para siempre.
  const [loadErr, setLoadErr] = useState(false);
  const [loadTick, setLoadTick] = useState(0);
  useEffect(() => {
    let stop = false;
    setLoadErr(false);
    const run = async (attempt) => {
      try {
        const [p, pl, pr, r] = await Promise.all([
          api.get(`/students/${id}/profile`),
          api.get(`/students/${id}/plan`),
          api.get(`/students/${id}/progress`),
          api.get(`/students/${id}/recommendations`).catch(() => ({ data: { recommendations: [] } })),
        ]);
        if (stop) return;
        setProfile(p.data);
        setPlan({ ruta: [], intervencion: [], mapa_clases: [], ...(pl.data || {}) });
        setProgress(pr.data);
        setRecs(r.data.recommendations || []);
      } catch (e) {
        if (stop) return;
        if (attempt < 3) setTimeout(() => { if (!stop) run(attempt + 1); }, 1500 * (attempt + 1));
        else setLoadErr(true);
      }
    };
    run(0);
    return () => { stop = true; };
  }, [id, loadTick]);

  if (loadErr && (!profile || !plan || !progress)) {
    return (
      <div className="premium-card p-10 text-center text-[#6E7591] space-y-3" data-testid="estudiante-load-error">
        <p>No se pudo cargar el perfil del estudiante.</p>
        <button type="button" onClick={() => setLoadTick((t) => t + 1)} className="jasq-btn-primary" data-testid="estudiante-reintentar">Reintentar</button>
      </div>
    );
  }
  if (!profile || !plan || !progress) return <p className="text-[#8A9499]" data-testid="estudiante-cargando">Cargando…</p>;

  const s = profile.student;
  const emotionalRadar = [
    { dim: "Motivación", value: profile.emotional.motivacion ?? 0 },
    { dim: "Confianza", value: profile.emotional.confianza ?? 0 },
    { dim: "Perseverancia", value: profile.emotional.perseverancia ?? 0 },
    { dim: "Atención", value: profile.emotional.atencion ?? 0 },
    { dim: "Calma (-Ansiedad)", value: profile.emotional.ansiedad != null ? 100 - profile.emotional.ansiedad : 0 },
    { dim: "Tolerancia (-Frust.)", value: profile.emotional.frustracion != null ? 100 - profile.emotional.frustracion : 0 },
  ];
  const academicRadar = Object.entries(profile.academic || {}).map(([k, v]) => {
    const label = k.replace(/_/g, " ");
    return { dim: label.charAt(0).toUpperCase() + label.slice(1), value: v };
  });

  return (
    <div className="space-y-6" data-testid="estudiante-detalle-page">
      <Link to={backRoute} className="inline-flex items-center gap-1.5 text-sm text-[#5C666B] hover:text-[#C87A5B]">
        <ArrowLeft size={15} /> Volver a estudiantes
      </Link>

      {/* Header */}
      <div className="premium-card p-6 flex flex-wrap items-start gap-6">
        <img src={s.avatar} alt="" className="w-20 h-20 rounded-full bg-[#F3EFEA] border border-[#E8E2D9]" />
        <div className="flex-1 min-w-[240px]">
          <span className="tag-premium">Perfil integrado</span>
          <h1 className="font-display text-3xl font-medium text-[#2C3539] mt-2">{s.name}</h1>
          <p className="text-sm text-[#5C666B] mt-1">{s.grade} · {s.age} años · {s.email}</p>
          <div className="flex flex-wrap gap-2 mt-3">
            <span className="tag-pill bg-[#C87A5B]/15 text-[#9A5B42]">{profile.qualitative_status}</span>
            <span className="tag-pill bg-[#F3EFEA] text-[#5C666B]">Crecimiento {profile.growth_index}%</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to={`${detailBase}/evaluar`}
            data-testid="cta-evaluar"
            className="inline-flex items-center gap-2 bg-[#C87A5B] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#B36B4F]"
          ><ClipboardList size={15} /> Evaluar 1:1</Link>
          <Link
            to={`${detailBase}/plan/editar`}
            data-testid="cta-editar-plan"
            className="inline-flex items-center gap-2 bg-white border border-[#E8E2D9] text-[#2C3539] px-4 py-2 rounded-md text-sm font-medium hover:bg-[#F3EFEA]"
          ><PenSquare size={15} /> Armar ruta</Link>
          <button
            type="button"
            onClick={generarRutaEnsenanza}
            disabled={rutaBusy}
            data-testid="cta-generar-ruta-ensenanza"
            className="inline-flex items-center gap-2 bg-[#A43790] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#8A2E79] disabled:opacity-60"
          >
            {rutaBusy ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} />}
            {rutaBusy ? "Generando…" : "Generar Ruta de Enseñanza"}
          </button>
          <button
            type="button"
            onClick={generarTodosInformes}
            disabled={allBusy || rutaBusy}
            data-testid="cta-generar-todos-informes"
            className="inline-flex items-center gap-2 bg-[#1F2547] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#141A35] disabled:opacity-60"
          >
            {allBusy ? <Loader2 size={15} className="animate-spin" /> : <ClipboardList size={15} />}
            {allBusy ? "Generando todos…" : "Generar Todos los Informes"}
          </button>
        </div>
      </div>

      <StudentExtras studentId={id} />

      {rutaReport?.pdf_url && (
        <div className="premium-card p-4 flex items-center justify-between gap-4" data-testid="ruta-ensenanza-ready">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-full bg-[#A43790] text-white inline-flex items-center justify-center"><FileText size={16} /></span>
            <div>
              <p className="text-sm font-bold text-[#1F2547]">Ruta de Enseñanza generada</p>
              <p className="text-xs text-[#6E7591]">Informe PDF con marca JASQ® · redactado por IA</p>
            </div>
          </div>
          <a
            href={rutaReport.pdf_url}
            target="_blank"
            rel="noreferrer"
            data-testid="ruta-ensenanza-download"
            className="inline-flex items-center gap-2 bg-white border border-[#A43790] text-[#A43790] px-4 py-2 rounded-md text-sm font-extrabold hover:bg-[#FFE9F0]"
          ><FileText size={15} /> Abrir / Descargar PDF</a>
        </div>
      )}

      {((queueInfo && queueInfo.active > 0) || serverBusy) && (
        <div className="space-y-3" data-testid="queue-area">
        {queueInfo && queueInfo.active > 0 && (
          <div className="rounded-2xl neo-flat p-4" data-testid="queue-progress">
            <div className="flex items-center justify-between gap-2 text-sm font-bold text-[#1F2547]">
              <span className="inline-flex items-center gap-2">{queuePaused ? null : <Loader2 size={14} className="animate-spin" />} {queueInfo.active} informe(s) {queuePaused ? "en espera" : "en proceso"}</span>
              {!queuePaused && <span className="text-[#9C7A18]" data-testid="queue-eta">Tiempo estimado {fmtEta(queueInfo.eta_seconds) || "calculando…"}</span>}
            </div>
            <div className="mt-2 h-2 rounded-full bg-white overflow-hidden">
              <div className="h-full rounded-full bg-[#F5B82E] transition-all" style={{ width: `${Math.round(((queueInfo.total - queueInfo.active) / Math.max(queueInfo.total, 1)) * 100)}%` }} />
            </div>
            <p className="text-[11px] text-[#6E7591] mt-1.5">
              {queuePaused ? "La cola de informes está en pausa: se generarán cuando el administrador la reanude (Tecnología → Cola de informes). " : ""}Se generan en segundo plano; puedes salir de esta pantalla y volver. {queueInfo.queue_total > 0 && `Hay ${queueInfo.queue_total} informe(s) en cola en el sistema.`}
            </p>
          </div>
        )}
        {serverBusy && (
          <div className="rounded-2xl neo-flat p-3 text-xs font-bold text-[#2C49A0]" data-testid="queue-server-busy">
            Tus informes siguen en progreso. Reconectando automáticamente…
          </div>
        )}
        </div>
      )}

      {/* v5.2.0 · Indicadores del estudiante agrupados en pestañas */}
      <div className="neo-pressed rounded-2xl p-1 flex flex-wrap gap-1" role="tablist" data-testid="estudiante-tabs">
        {TABS.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`flex-1 min-w-[120px] px-4 py-2 rounded-xl text-sm font-extrabold transition-all ${tab === t.id ? "neo-flat text-[#A43790]" : "text-[#6E7591] hover:text-[#1F2547]"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "resumen" && (
        <div className="space-y-6" data-testid="tab-panel-resumen">
          <DiagnosticoPanel studentId={id} section="resumen" />
      {/* AI Recommendations */}
      <div className="premium-card p-6" data-testid="ai-recommendations">
        <div className="flex items-center gap-2 mb-3">
          <Lightbulb size={17} className="text-[#B8985C]" />
          <h3 className="font-display text-lg font-medium text-[#2C3539]">Recomendaciones para armar la ruta de mejora</h3>
          <span className="tag-premium ml-auto"><Sparkles size={11} /> Sugerencias</span>
        </div>
        <p className="text-sm text-[#5C666B] mb-4">
          Sugerencias automáticas basadas en este perfil. Llévalas al constructor de plan para aplicarlas.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {recs.map((r, i) => (
            <div key={i} className="p-4 bg-[#FAF7F2] border border-[#E8E2D9] rounded-md" data-testid={`rec-${i}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`tag-pill ${
                  r.priority === "alta" ? "bg-[#B85C5C]/15 text-[#8a4646]" :
                  r.priority === "media" ? "bg-[#D4A373]/20 text-[#7a5d3e]" :
                  "bg-[#7A9E7E]/15 text-[#5b7e60]"
                }`}>{r.priority}</span>
                <span className="text-[10px] uppercase tracking-[0.12em] text-[#8A9499] font-bold">{r.type}</span>
              </div>
              <p className="text-sm font-medium text-[#2C3539]">{r.title}</p>
              <p className="text-xs text-[#5C666B] mt-1.5 leading-relaxed">{r.action}</p>
              <p className="text-[10px] uppercase tracking-[0.14em] text-[#8A9499] font-bold mt-2">⏱ {r.duration}</p>
            </div>
          ))}
        </div>
        <Link
          to={`${detailBase}/plan/editar`}
          className="mt-5 inline-flex items-center gap-2 bg-[#B8985C] text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-[#9F8049] transition-colors"
          data-testid="cta-build-route"
        ><PenSquare size={15} /> Aplicar y armar ruta de mejora</Link>
      </div>
        </div>
      )}

      {tab === "perfiles" && (
        <div className="space-y-6" data-testid="tab-panel-perfiles">
      {/* Profile radars */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="premium-card p-6" data-testid="emotional-radar">
          <div className="flex items-center gap-2 mb-1">
            <HeartPulse size={17} className="text-[#C87A5B]" />
            <h3 className="font-display text-lg font-medium text-[#2C3539]">Perfil emocional</h3>
          </div>
          <p className="text-xs text-[#8A9499] mb-2">Test emocional–perceptivo</p>
          <ProfileRadar data={emotionalRadar} color="#C87A5B" testid="chart-emotional-radar" />
        </div>
        <div className="premium-card p-6" data-testid="academic-radar">
          <div className="flex items-center gap-2 mb-1">
            <Brain size={17} className="text-[#8BA8B5]" />
            <h3 className="font-display text-lg font-medium text-[#2C3539]">Perfil académico</h3>
          </div>
          <p className="text-xs text-[#8A9499] mb-2">Por área temática</p>
          <ProfileRadar data={academicRadar} color="#8BA8B5" testid="chart-academic-radar" />
        </div>
        <div className="premium-card p-6" data-testid="integrated-radar">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={17} className="text-[#8CA894]" />
            <h3 className="font-display text-lg font-medium text-[#2C3539]">Perfil integrado</h3>
          </div>
          <p className="text-xs text-[#8A9499] mb-2">Cognitivo + emocional</p>
          <ProfileRadar data={profile.integrated} color="#8CA894" testid="chart-integrated-radar" />
        </div>
      </div>
          <DiagnosticoPanel studentId={id} section="discrepancia" />
        </div>
      )}

      {tab === "evolucion" && (
        <div className="space-y-6" data-testid="tab-panel-evolucion">
      {/* Progress chart */}
      <div className="premium-card p-6" data-testid="progress-chart">
        <h3 className="font-display text-lg font-medium text-[#2C3539] mb-1">Evolución semanal</h3>
        <p className="text-sm text-[#5C666B] mb-4">Crecimiento cognitivo y emocional en el tiempo</p>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={progress.weekly_growth}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8E2D9" />
              <XAxis dataKey="week" tick={{ fill: "#5C666B", fontSize: 11 }} />
              <YAxis domain={[0, 100]} tick={{ fill: "#5C666B", fontSize: 11 }} />
              <Tooltip contentStyle={{ backgroundColor: "white", border: "1px solid #E8E2D9", borderRadius: 8, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="cognitivo" stroke="#8BA8B5" strokeWidth={2.5} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="emocional" stroke="#C87A5B" strokeWidth={2.5} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Plan personalizado */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="premium-card p-6 lg:col-span-2" data-testid="learning-route">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen size={17} className="text-[#C87A5B]" />
            <h3 className="font-display text-lg font-medium text-[#2C3539]">Ruta de aprendizaje</h3>
          </div>
          <ol className="space-y-3">
            {plan.ruta.map((r, idx) => (
              <li key={r.id} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                    r.status === "completado" ? "bg-[#7A9E7E] text-white" :
                    r.status === "en_curso" ? "bg-[#C87A5B] text-white" :
                    "bg-[#F3EFEA] text-[#5C666B] border border-[#E8E2D9]"
                  }`}>{idx + 1}</div>
                  {idx < plan.ruta.length - 1 && <div className="w-px flex-1 bg-[#E8E2D9] mt-1" />}
                </div>
                <div className="pb-4 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-medium text-[#2C3539]">{r.title}</p>
                    <span className="text-xs text-[#8A9499]">{r.weeks} sem</span>
                  </div>
                  <p className="text-xs text-[#5C666B] mt-1">{r.topics.join(" · ")}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        <div className="premium-card p-6" data-testid="intervention-plan">
          <h3 className="font-display text-lg font-medium text-[#2C3539] mb-3">Plan de intervención</h3>
          <ul className="space-y-3">
            {plan.intervencion.map((i) => (
              <li key={i.id} className="border-l-2 border-[#C87A5B] pl-3">
                <p className="text-xs tracking-[0.1em] uppercase font-bold text-[#C87A5B]">{i.tipo}</p>
                <p className="text-sm text-[#2C3539] mt-1">{i.accion}</p>
              </li>
            ))}
          </ul>

          <h3 className="font-display text-lg font-medium text-[#2C3539] mb-3 mt-6">Mapa de clases</h3>
          <div className="grid grid-cols-4 gap-2">
            {plan.mapa_clases.map((m) => (
              <div
                key={m.week}
                title={m.title}
                className={`aspect-square rounded-md text-[10px] font-medium flex items-center justify-center ${
                  m.completed ? "bg-[#7A9E7E] text-white" :
                  m.current ? "bg-[#C87A5B] text-white ring-4 ring-[#C87A5B]/20" :
                  "bg-[#F3EFEA] text-[#8A9499]"
                }`}
              >S{m.week}</div>
            ))}
          </div>
        </div>
      </div>
      {/* Milestones */}
      <div className="premium-card p-6" data-testid="milestones">
        <h3 className="font-display text-lg font-medium text-[#2C3539] mb-4">Hitos del estudiante</h3>
        <ul className="space-y-3">
          {progress.milestones.map((m, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="tag-pill bg-[#F3EFEA] text-[#5C666B] mt-0.5">{m.date}</span>
              <span className="text-sm text-[#2C3539]">{m.title}</span>
            </li>
          ))}
        </ul>
      </div>
        </div>
      )}

      {tab === "informes" && (
        <div className="space-y-6" data-testid="tab-panel-informes">
      {/* v5.2.0 · Expediente como ÁRBOL desplegable (sin mostrar errores) */}
      <ReportTree
        items={expediente}
        labels={REPORT_LABELS}
        baseRoute={baseRoute}
        fmtEta={fmtEta}
        onRetry={reintentarInforme}
      />
        </div>
      )}
    </div>
  );
}
